import React from 'react';
import { format } from 'date-fns';
import { Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PricingAnalysisRow, Competitor } from '@/types/competitor';

interface CompetitorBreakdownProps {
  row: PricingAnalysisRow;
  getOTABreakdownForCompetitors: (row: PricingAnalysisRow) => Array<{
    competitor: Competitor;
    otaPrices: Array<{
      channel: string;
      price: number;
      formattedPrice: string;
      channelColor: string;
      roomType?: string;
    }>;
    average: number;
    formattedAverage: string;
  }>;
  formatPrice: (value: number) => string;
}

export function CompetitorBreakdown({ 
  row, 
  getOTABreakdownForCompetitors,
  formatPrice 
}: CompetitorBreakdownProps) {
  const competitorDataList = getOTABreakdownForCompetitors(row);
  
  if (competitorDataList.length === 0) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-4 text-center">
          <p className="text-sm text-amber-700">
            No competitor data available for this date.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {competitorDataList.map(competitorData => {
        const { competitor, otaPrices, average } = competitorData;

        return (
          <Card key={competitor.id} className="border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-5 w-5 text-primary" />
                <h4 className="font-semibold">OTA Breakdown: {competitor.name}</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Detailed pricing across all tracked OTA channels for {format(new Date(row.date), 'yyyy-MM-dd')}
              </p>

              <div className="mb-4 overflow-hidden rounded-lg border">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="p-2 text-left text-sm font-medium text-muted-foreground">OTA Channel</th>
                      <th className="p-2 text-left text-sm font-medium text-muted-foreground">Room Type</th>
                      <th className="p-2 text-center text-sm font-medium text-muted-foreground">Price</th>
                      <th className="p-2 text-center text-sm font-medium text-muted-foreground">vs Your Price</th>
                      <th className="p-2 text-center text-sm font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otaPrices.map((ota, index) => {
                      const diff = ota.price - row.yourPrice;
                      const diffPercent = ((diff / row.yourPrice) * 100).toFixed(1);
                      const formattedDiff = diff < 0 ? `-${formatPrice(Math.abs(diff))}` : `+${formatPrice(Math.abs(diff))}`;
                      const formattedDiffPercent = diff < 0 ? `(${diffPercent}%)` : `(+${diffPercent}%)`;

                      return (
                        <tr key={`${ota.channel}-${ota.roomType || index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-muted/30'}>
                          <td className="p-2 border-b">
                            <div className="flex items-center gap-2">
                              <div
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: ota.channelColor }}
                              />
                              <span className="font-medium">{ota.channel}</span>
                            </div>
                          </td>
                          <td className="p-2 border-b">
                            <span className="text-sm text-muted-foreground">{ota.roomType || 'N/A'}</span>
                          </td>
                          <td className="p-2 text-center border-b font-semibold">
                            {ota.formattedPrice}
                          </td>
                          <td className="p-2 text-center border-b">
                            <span className={diff < 0 ? 'text-green-600' : 'text-destructive'}>
                              {formattedDiff} <span className="text-xs">{formattedDiffPercent}</span>
                            </span>
                          </td>
                          <td className="p-2 text-center border-b">
                            <Badge
                              variant="outline"
                              className={diff < 0 ? 'border-yellow-300 bg-yellow-50 text-yellow-700' : 'border-green-300 bg-green-50 text-green-700'}
                            >
                              {diff < 0 ? 'Lower' : 'Higher'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-3 gap-4 bg-muted/30 rounded-lg p-3">
                <div className="text-center">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Average Price</div>
                  <div className="text-xl font-bold">{competitorData.formattedAverage}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Your Price</div>
                  <div className="text-xl font-bold">{formatPrice(row.yourPrice)}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Difference</div>
                  <div className={`text-xl font-bold ${average > row.yourPrice ? 'text-green-600' : 'text-destructive'}`}>
                    {average > row.yourPrice ? '-' : '+'}{formatPrice(Math.abs(average - row.yourPrice))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </>
  );
}
